// Organization Info Tab with Payment Accounts Editor
import React, { useState, useEffect } from 'react';
import { View, ScrollView, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { GlassFormInput } from '@/components/admin/redesign';
import { DEFAULT_ORGANIZATION_FORM_STATE } from '@/constants/admin/organizationFormDefaults';
import { IOrganization } from '@/types/admin/organizations';
import { useOrganizationFormStyles } from './organizationFormStyles';

interface OrganizationInfoTabProps {
  organization: IOrganization;
  onUpdate: (organization: IOrganization) => Promise<void>;
  onDelete: () => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const OrganizationInfoTab: React.FC<OrganizationInfoTabProps> = ({
  organization,
  onUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}) => {
  const styles = useOrganizationFormStyles();

  const [formData, setFormData] = useState({
    name: organization.name,
    domain: organization.domain,
    paymentAccounts:
      organization.paymentAccounts && organization.paymentAccounts.length > 0
        ? organization.paymentAccounts
        : DEFAULT_ORGANIZATION_FORM_STATE.paymentAccounts,
  });

  const [errors, setErrors] = useState({
    name: '',
    domain: '',
  });

  useEffect(() => {
    setFormData({
      name: organization.name,
      domain: organization.domain,
      paymentAccounts:
        organization.paymentAccounts && organization.paymentAccounts.length > 0
          ? organization.paymentAccounts
          : DEFAULT_ORGANIZATION_FORM_STATE.paymentAccounts,
    });
  }, [organization]);

  const validate = (): boolean => {
    const newErrors = { name: '', domain: '' };

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.domain)) {
      newErrors.domain = 'Domain must contain only lowercase letters, numbers, and hyphens';
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.domain;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const updatedOrg: IOrganization = {
        ...organization,
        name: formData.name.trim(),
        domain: formData.domain.trim(),
        paymentAccounts: formData.paymentAccounts,
      };

      await onUpdate(updatedOrg);
    } catch (error) {
      throw error;
    }
  };

  const handlePaymentAccountChange = (index: number, accountNumber: string) => {
    const newAccounts = [...formData.paymentAccounts];
    newAccounts[index] = {
      ...newAccounts[index],
      accountNumber: accountNumber || null,
    };
    setFormData({ ...formData, paymentAccounts: newAccounts });
  };

  const hasChanges =
    formData.name !== organization.name ||
    formData.domain !== organization.domain ||
    JSON.stringify(formData.paymentAccounts) !== JSON.stringify(organization.paymentAccounts);

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* About Organization Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>About Organization</Text>

        <GlassFormInput
          label="Organization Name"
          value={formData.name}
          onChangeText={(text) => setFormData({ ...formData, name: text })}
          placeholder="Organization name"
          error={errors.name}
          required
        />

        <GlassFormInput
          label="Organization Domain"
          value={formData.domain}
          onChangeText={(text) => setFormData({ ...formData, domain: text.toLowerCase() })}
          placeholder="organization-domain"
          autoCapitalize="none"
          error={errors.domain}
          required
        />
      </View>

      {/* Payment Accounts Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Payment Accounts</Text>

        {formData.paymentAccounts.map((account, index) => (
          <View key={account.name} style={styles.paymentAccountField}>
            <GlassFormInput
              label={account.description}
              value={account.accountNumber?.toString() || ''}
              onChangeText={(text) => handlePaymentAccountChange(index, text)}
              placeholder="Account number"
              keyboardType="numeric"
            />
          </View>
        ))}
      </View>

      {/* Actions */}
      <View style={[styles.actionsContainer, { justifyContent: 'space-between' }]}>
        <TouchableOpacity
          style={[styles.button, styles.deleteButton, { flex: 1 }]}
          onPress={onDelete}
          disabled={isDeleting || isUpdating}
          activeOpacity={0.7}
        >
          {isDeleting ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <Text style={styles.deleteButtonText}>Delete Organization</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.button,
            styles.saveButton,
            { flex: 1 },
            (!hasChanges || isUpdating) && styles.disabledButton,
          ]}
          onPress={handleSave}
          disabled={!hasChanges || isUpdating || isDeleting}
          activeOpacity={0.7}
        >
          {isUpdating ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <Text style={styles.saveButtonText}>Save</Text>
          )}
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};
